import { faEdit, faPlay, faProjectDiagram, faRedoAlt, faSquare, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { AssignDialogProps } from '../../components/AssignDialog'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FlipCard } from '../../components/FlipCard'
import ProtectedButton from '../../components/ProtectedButton'
import { SelectDialogProps } from '../../components/SelectDialog'
import Loading from '../../layout/Loading'
import ProxyService from '../../service/ProxyService'
import TlsService from '../../service/TlsService'
import { expandRowFunction, formatBooleanColumn, formatProxyStatusColumn } from '../../utils/DatatableUtil'
import GatewayType from '../GatewayType'

const Proxies: GatewayType = () => {
  const { t } = useTranslation()
  const [proxies, setProxies] = useState([] as any[])
  const [keysWithCerts, setKeysWithCerts] = useState([] as any[])

  const { userPermissions, isClusterSlotReserved } = useContext(AppContext)
  const context = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadCerts() {
      await TlsService.getTlsKeysWithCerts(setKeysWithCerts)
      await ProxyService.getProxies(setProxies)
    }

    loadCerts().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    const { confirmDialogPropsSetter, editDialogPropsSetter, assignDialogPropsSetter } = context
    return [
      {
        text: t('name.text'),
        dataField: 'name',
        sort: true,
        classes: 'position-relative',
        formatter: (cellData: any, row: any) => {
          return AuthService.hasPermission(userPermissions, Authorization.services.proxies.edit) ? (
            <ProtectedButton
              variant='link'
              tooltip={t('edit')}
              className='text-decoration-none pl-0 stretched-link text-left'
              onClick={() => editProxyDialog(t, editDialogPropsSetter, setProxies, setKeysWithCerts, keysWithCerts, row)}>
              <FontAwesomeIcon icon={faEdit} /> {cellData}
            </ProtectedButton>
          ) : (
            cellData
          )
        },
      },
      {
        text: t('type.text'),
        dataField: 'type',
        sort: true,
      },
      {
        text: t('port.text'),
        dataField: 'port',
        sort: true,
      },
      {
        text: t('status'),
        dataField: 'status',
        align: 'center',
        formatter: (cell: any, row: any) => formatProxyStatusColumn(cell, row),
        sort: true,
      },
      {
        text: t('tlsEnabled'),
        dataField: 'tlsEnabled',
        align: 'center',
        formatter: (cell: any) => formatBooleanColumn(cell),
        sort: true,
      },
      {
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (_cellData: any, row: any) => (
          <>
            <ProtectedButton
              permissions={Authorization.services.proxies.runtime}
              shouldRender={row.status === 'STOPPED' && isClusterSlotReserved}
              variant='link'
              tooltip={t('proxy.start')}
              className='text-decoration-none text-left ml-3 mr-4'
              onClick={() =>
                proxyAction(
                  t,
                  t('proxy.start'),
                  t('proxy.startBody', { name: row.name }),
                  ProxyService.startProxy,
                  [row.id, setProxies, setLoading],
                  confirmDialogPropsSetter
                )
              }>
              <FontAwesomeIcon icon={faPlay} className='text-primary' />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.services.proxies.runtime}
              shouldRender={row.status !== 'STOPPED' && row.status !== 'DELETED' && isClusterSlotReserved}
              variant='link'
              tooltip={t('proxy.restart')}
              className='text-decoration-none text-left mr-2'
              onClick={() =>
                proxyAction(
                  t,
                  t('proxy.restart'),
                  t('proxy.restartBody', { name: row.name }),
                  ProxyService.restartProxy,
                  [row.id, setProxies, setLoading],
                  confirmDialogPropsSetter
                )
              }>
              <FontAwesomeIcon icon={faRedoAlt} className='text-warning' />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.services.proxies.runtime}
              shouldRender={row.status !== 'STOPPED' && isClusterSlotReserved}
              variant='link'
              tooltip={t('proxy.stop')}
              className='text-decoration-none text-left mr-2'
              onClick={() =>
                proxyAction(
                  t,
                  t('proxy.stop'),
                  t('proxy.stopBody', { name: row.name }),
                  ProxyService.stopProxy,
                  [row.id, setProxies, setLoading],
                  confirmDialogPropsSetter
                )
              }>
              <FontAwesomeIcon icon={faSquare} className='text-danger' />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.services.proxies.edit}
              variant='link'
              tooltip={t('proxy.assignApps')}
              className='text-decoration-none text-right mr-2'
              onClick={() => assignAppssDialog(t, assignDialogPropsSetter, setProxies, row)}>
              <FontAwesomeIcon icon={faProjectDiagram} />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.services.proxies.delete}
              variant='link'
              tooltip={t('delete')}
              className='text-danger text-link text-decoration-none text-right'
              onClick={() => deleteProxyDialog(t, confirmDialogPropsSetter, setProxies, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          </>
        ),
      },
    ]
  }, [t, userPermissions, context, isClusterSlotReserved, keysWithCerts])

  const expandRow = expandRowFunction(renderer, nonExpandable, t)

  const defaultSorted = [
    {
      dataField: 'name',
      order: 'asc',
    },
  ]

  function nonExpandable() {
    return proxies.filter((proxy) => proxy.advanced === undefined).map((proxy) => proxy.id)
  }

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={proxies}
        columns={columns}
        caption={t('proxy.text_plural')}
        helpLink={t('proxy.helpLink')}
        expandRow={expandRow}
        defaultSorted={defaultSorted}
        captionButtons={[
          {
            onClick: () => createProxyDialog(t, context.editDialogPropsSetter, setProxies, setKeysWithCerts, keysWithCerts),
            text: t('new'),
            permissions: Authorization.services.proxies.edit,
          },
        ]}
      />
    </Loading>
  )
}

function proxyAction(t: TFunction, header: string, body: string, onSubmit: any, onSubmitExtraArgs: any[], confirmDialogPropsSetter: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header,
    body,
    submitText: t('yes'),
    onSubmit,
    onSubmitExtraArgs,
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function renderer(row: any, rowIndex: any, ...args: any[]) {
  const t: TFunction = args[0]
  const columns: any[] = [
    {
      text: t('attribute'),
      dataField: 'attribute',
      classes: 'col-sm-2',
    },
    {
      text: t('value'),
      dataField: 'value',
      classes: 'col-sm-10',
      formatter: (cell: any) => cell.split('\n').map((advanced: string) => <p>{advanced}</p>),
    },
  ]

  const data: any[] = [
    {
      attribute: t('advanced.text'),
      value: row['advanced'],
    },
  ]
  return (
    <React.Fragment key={row.name}>
      <DataTable
        data={data}
        columns={columns}
        searchable={false}
        headerClasses='d-none'
        striped={false}
        hover={false}
        bodyClasses='container tr-row'
        condensed={true}
        noDataIndication={() => <span className='text-center'>{t('proxy.noAttributeFound')}</span>}
      />
    </React.Fragment>
  )
}

function createProxyDialog(
  t: TFunction,
  editDialogPropsSetter: any,
  setProxies: React.Dispatch<any>,
  setKeysWithCerts: React.Dispatch<any>,
  keysWithCerts: any[]
) {
  const numberOfKeys = keysWithCerts.length
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('proxy.createHeader'),
    onSubmit: ProxyService.createProxyConfig,
    onSubmitExtraArgs: [setProxies, setKeysWithCerts, t],
    data: (custom: any[]) => [
      {
        id: 'id',
        type: 'hidden',
        render: false,
      },
      {
        id: 'name',
        type: 'text',
        label: t('name.text'),
        placeholder: t('name.text'),
        requiredMessage: t('name.inValidMessage'),
        required: true,
      },
      {
        id: 'type',
        type: 'select',
        options: [
          { label: 'Forward', value: 'FORWARD' },
          { label: 'Reverse', value: 'REVERSE' },
        ],
        label: t('type.text'),
        value: 'FORWARD',
        required: true,
      },
      {
        id: 'port',
        type: 'number',
        label: t('port.text'),
        placeholder: t('port.text'),
        required: true,
        requiredMessage: t('port.inValidMessage'),
        min: '1',
        minInvalidMessage: t('port.rangeInValidMessage'),
        max: '65535',
        maxInvalidMessage: t('port.rangeInValidMessage'),
      },
      {
        groupLabel: t('tlsEnabled'),
        groupName: 'tlsEnabled',
        type: 'switch',
        checkboxes: [
          {
            id: 'tlsEnabled',
            onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.toString(), custom[1], custom[2], custom[3]]),
            value: custom[0] === 'true',
          },
        ],
      },
      {
        groupLabel: t('certificates.usesDefaultCertificate'),
        groupName: 'Use default certificate',
        type: 'switch',
        render: numberOfKeys > 0 && custom[0] === 'true',
        checkboxes: [
          {
            id: 'usesDefaultCertificate',
            onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], e.toString(), custom[2], custom[3]]),
            value: custom[1] === 'true',
          },
        ],
      },
      {
        id: 'keyId',
        type: 'select',
        options: keysWithCerts.map((key) => ({ label: key.name, value: key.id })),
        value: custom[2],
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], custom[1], e.target.value, '']),
        label: t('keys.keyName'),
        required: true,
        requiredMessage: t('invalidMessage', { label: 'Key' }),
        render: custom[1] === 'false' && custom[0] === 'true',
      },
      {
        id: 'certId',
        type: 'select',
        options:
          numberOfKeys > 0
            ? [
                { label: 'Select a Certificate', value: '' },
                ...keysWithCerts
                  .find((key) => key.id === Number(custom[2]))
                  ?.certs.map((cert: any) => ({ label: `${cert.subjectDn} (${numberToHex(cert.serialNumber)})`, value: cert.id })),
              ]
            : [],
        value: custom[3],
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], custom[1], custom[2], e.target.value]),
        label: t('certificate.text'),
        required: true,
        requiredMessage: t('invalidMessage', { label: 'Certificate' }),
        render: custom[1] === 'false' && custom[0] === 'true' && custom[2] !== '0',
      },
      {
        id: 'advanced',
        type: 'textarea',
        label: t('advanced.text'),
        placeholder: t('advanced.placeHolder'),
      },
    ],
    initCustom: ['true', 'true', numberOfKeys > 0 ? keysWithCerts[0].id : '-1', ''],
    labelGridSize: 3,
  }

  editDialogPropsSetter(editDialogProps)
}

function editProxyDialog(
  t: TFunction,
  editDialogPropsSetter: any,
  setProxies: React.Dispatch<any>,
  setKeysWithCerts: React.Dispatch<any>,
  keysWithCerts: any[],
  row: any
) {
  const numberOfKeys = keysWithCerts.length
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('proxy.editHeader'),
    onSubmit: ProxyService.updateProxyConfig,
    onSubmitExtraArgs: [setProxies, setKeysWithCerts, t],
    data: (custom: any[]) => {
      return [
        {
          id: 'id',
          value: row.id,
          type: 'hidden',
          render: false,
        },
        {
          id: 'name',
          value: row.name,
          type: 'text',
          label: t('name.text'),
          placeholder: t('name.text'),
          requiredMessage: t('name.inValidMessage'),
          required: true,
        },
        {
          id: 'type',
          type: 'select',
          options: [
            { label: 'Forward', value: 'FORWARD' },
            { label: 'Reverse', value: 'REVERSE' },
          ],
          value: row.type,
          label: t('type.text'),
          required: true,
        },
        {
          id: 'port',
          type: 'number',
          label: t('port.text'),
          value: row.port,
          placeholder: t('port.text'),
          required: true,
          requiredMessage: t('port.inValidMessage'),
          min: '1',
          minInvalidMessage: t('port.rangeInValidMessage'),
          max: '65535',
          maxInvalidMessage: t('port.rangeInValidMessage'),
        },
        {
          groupLabel: t('tlsEnabled'),
          groupName: 'tlsEnabled',
          type: 'switch',
          checkboxes: [
            {
              id: 'tlsEnabled',
              onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.toString(), custom[1], custom[2], custom[3]]),
              value: custom[0] === 'true',
            },
          ],
        },
        {
          groupLabel: t('certificates.usesDefaultCertificate'),
          groupName: 'Use default certificate',
          type: 'switch',
          render: numberOfKeys > 0 && custom[0] === 'true',
          checkboxes: [
            {
              id: 'usesDefaultCertificate',
              onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], e.toString(), custom[2], custom[3]]),
              value: custom[1] === 'true',
            },
          ],
        },
        {
          id: 'keyId',
          type: 'select',
          options: keysWithCerts.map((key) => ({ label: key.name, value: key.id })),
          value: custom[2],
          onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], custom[1], e.target.value, '']),
          label: t('keys.keyName'),
          required: true,
          requiredMessage: t('invalidMessage', { label: 'Key' }),
          render: custom[1] === 'false' && custom[0] === 'true',
        },
        {
          id: 'certId',
          type: 'select',
          options:
            numberOfKeys > 0
              ? [
                  { label: 'Select a Certificate', value: '' },
                  ...keysWithCerts
                    .find((key) => key.id === Number(custom[2]))
                    ?.certs.map((cert: any) => ({ label: `${cert.subjectDn} (${numberToHex(cert.serialNumber)})`, value: cert.id })),
                ]
              : [],
          value: custom[3],
          onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], custom[1], custom[2], e.target.value]),
          label: t('certificate.text'),
          required: true,
          requiredMessage: t('invalidMessage', { label: 'Certificate' }),
          render: custom[1] === 'false' && custom[0] === 'true',
        },
        {
          id: 'advanced',
          type: 'textarea',
          value: row.advanced,
          label: t('advanced.text'),
          placeholder: t('advanced.placeHolder'),
        },
      ]
    },
    initCustom: [
      row.tlsEnabled?.toString(),
      row.usesDefaultCertificate?.toString(),
      row.keyId === undefined ? (numberOfKeys > 0 ? keysWithCerts[0].id : '-1') : row.keyId,
      row.certId === undefined ? undefined : row.certId,
    ],
    labelGridSize: 3,
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteProxyDialog(t: TFunction, confirmDialogPropsSetter: any, setProxies: React.Dispatch<any>, row: any) {
  if ('STOPPED' !== row.status) {
    const wariningDialogProps: ConfirmDialogProps = {
      show: true,
      header: t('proxy.editHeader'),
      body: t('proxy.stopBeforeDelete', { name: row.name }),
      submitText: t('ok'),
      showCancelButton: false,
    }
    confirmDialogPropsSetter(wariningDialogProps)
  } else {
    const deleteDialogProps: ConfirmDialogProps = {
      show: true,
      header: t('proxy.deleteHeader'),
      body: t('proxy.deleteBody', { name: row.name }),
      submitText: t('yes'),
      onSubmit: ProxyService.deleteProxyConfig,
      onSubmitExtraArgs: [row, setProxies, t],
      cancelText: t('no'),
    }
    confirmDialogPropsSetter(deleteDialogProps)
  }
}

function assignAppssDialog(t: TFunction, assignDialogPropsSetter: any, setProxies: React.Dispatch<any>, row: any) {
  ProxyService.getAppsWithAssigned(row).then((proxies) => {
    const assignDialogProps: AssignDialogProps = {
      show: true,
      header: t('proxy.assignHeader', { name: row.name }),
      onSubmit: ProxyService.assignApps,
      onSubmitExtraArgs: [row.id, row.name, setProxies, t],
      data: proxies,
      columns: [
        {
          text: t('name.text'),
          dataField: 'name',
          sort: true,
        },
        {
          text: t('type.text'),
          dataField: 'type',
          sort: true,
        },
        {
          dataField: 'isAssigned',
          text: t('assign'),
          isAssignField: true,
        },
      ],
    }
    assignDialogPropsSetter(assignDialogProps)
  })
}

function selectProxyDialog(t: TFunction, selectDialogPropsSetter: any, proxies: any[], onSubmit: any, onSubmitExtraArgs?: any[]) {
  const selectDialogProps: SelectDialogProps = {
    show: true,
    header: t('proxy.selectHeader'),
    data: proxies,
    keyField: 'name',
    label: t('proxy.selectHeader'),
    submitText: t('select'),
    onSubmit,
    onSubmitExtraArgs,
    cancelText: t('cancel'),
  }
  selectDialogPropsSetter(selectDialogProps)
}

function numberToHex(number: any) {
  return Number(number).toString(16).toUpperCase()
}

export const ProxyRuntimeFlipCard = (props: any) => {
  const { t } = useTranslation()
  const [proxies, setProxies] = useState([] as any[])
  const [keysWithCerts, setKeysWithCerts] = useState([] as any[])

  const [loading, setLoading] = useState(true)
  const { userPermissions, isClusterSlotReserved } = useContext(AppContext)
  const { editDialogPropsSetter, selectDialogPropsSetter, assignDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)

  const permissions = Authorization.services.proxies

  const { size = 'lg' } = props

  useEffect(() => {
    async function loadCerts() {
      await TlsService.getTlsKeysWithCerts(setKeysWithCerts)
      await ProxyService.getProxies(setProxies)
    }
    if (AuthService.hasPermission(userPermissions, permissions.runtime)) {
      loadCerts().finally(() => setLoading(false))
    }
  }, [userPermissions, permissions.runtime])

  const columns = useMemo(() => {
    return [
      {
        text: t('name.text'),
        dataField: 'name',
        classes: 'text-white',
        sort: true,
      },

      {
        text: t('port.text'),
        dataField: 'port',
        classes: 'text-white',
        sort: true,
      },
      {
        text: t('status'),
        dataField: 'status',
        align: 'center',
        formatter: (cell: any, row: any) => formatProxyStatusColumn(cell, row),
        sort: true,
      },
      {
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        classes: 'w-25 text-center',
        formatter: (cellData: any, row: any) => (
          <>
            <ProtectedButton
              permissions={permissions.runtime}
              shouldRender={row.status === 'STOPPED' && isClusterSlotReserved}
              variant='link'
              tooltip='Start Proxy'
              className='text-decoration-none'
              onClick={() =>
                proxyAction(
                  t,
                  t('proxy.start'),
                  t('proxy.startBody', { name: row.name }),
                  ProxyService.startProxy,
                  [row.id, setProxies, setLoading],
                  confirmDialogPropsSetter
                )
              }>
              <FontAwesomeIcon icon={faPlay} className='text-primary' />
            </ProtectedButton>
            <ProtectedButton
              permissions={permissions.runtime}
              shouldRender={row.status !== 'STOPPED' && row.status !== 'DELETED' && isClusterSlotReserved}
              variant='link'
              tooltip='Restart Proxy'
              className='text-decoration-none'
              onClick={() =>
                proxyAction(
                  t,
                  t('proxy.restart'),
                  t('proxy.restartBody', { name: row.name }),
                  ProxyService.restartProxy,
                  [row.id, setProxies, setLoading],
                  confirmDialogPropsSetter
                )
              }>
              <FontAwesomeIcon icon={faRedoAlt} className='text-warning' />
            </ProtectedButton>
            {'   '}
            <ProtectedButton
              permissions={permissions.runtime}
              shouldRender={row.status !== 'STOPPED' && isClusterSlotReserved}
              variant='link'
              tooltip='Stop Proxy'
              className='text-decoration-none'
              onClick={() =>
                proxyAction(
                  t,
                  t('proxy.stop'),
                  t('proxy.stopBody', { name: row.name }),
                  ProxyService.stopProxy,
                  [row.id, setProxies, setLoading],
                  confirmDialogPropsSetter
                )
              }>
              <FontAwesomeIcon icon={faSquare} className='text-danger' />
            </ProtectedButton>
          </>
        ),
      },
    ]
  }, [t, permissions.runtime, confirmDialogPropsSetter, isClusterSlotReserved])

  return (
    <FlipCard
      permissions={permissions.runtime}
      headerText={t('proxy.text')}
      headerLink='./services/proxies'
      size={size}
      flipFrontContent={
        <Loading isLoading={loading}>
          <DataTable data={proxies} columns={columns} searchable={false} />
        </Loading>
      }
      flipBackContent={
        <ButtonGroup vertical>
          <ProtectedButton
            permissions={permissions.edit}
            variant='link'
            className='m-1'
            onClick={() => createProxyDialog(t, editDialogPropsSetter, setProxies, setKeysWithCerts, keysWithCerts)}>
            {t('proxy.createHeader')}
          </ProtectedButton>

          <ProtectedButton
            permissions={permissions.edit}
            variant='link'
            className='m-1'
            onClick={() =>
              selectProxyDialog(t, selectDialogPropsSetter, proxies, editProxyDialog, [t, editDialogPropsSetter, setProxies, setKeysWithCerts, keysWithCerts])
            }>
            {t('proxy.editHeader')}
          </ProtectedButton>

          <ProtectedButton
            permissions={permissions.edit}
            variant='link'
            className='m-1'
            onClick={() => selectProxyDialog(t, selectDialogPropsSetter, proxies, assignAppssDialog, [t, assignDialogPropsSetter])}>
            {t('proxy.assignApps')}
          </ProtectedButton>

          <ProtectedButton
            permissions={permissions.delete}
            variant='link'
            className='m-1'
            onClick={() => selectProxyDialog(t, selectDialogPropsSetter, proxies, deleteProxyDialog, [t, confirmDialogPropsSetter, setProxies])}>
            {t('proxy.deleteHeader')}
          </ProtectedButton>
        </ButtonGroup>
      }
    />
  )
}

export default Proxies
